import Layout from "@/components/Layout";
import MembershipComparison from "@/components/MembershipComparison";
import MembershipTestimonials from "@/components/MembershipTestimonials";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Calendar, CreditCard, Gift, Users, ArrowRight, Sparkles } from "lucide-react";
const Membership = () => {
  const perks = [{
    icon: Calendar,
    title: "Priority Booking",
    description: "Skip the wait with priority access to appointments and same-day availability"
  }, {
    icon: CreditCard,
    title: "Flexible Credits",
    description: "Use credits for any treatment, rollover unused credits, and stack for bigger services"
  }, {
    icon: Gift,
    title: "Member Discounts",
    description: "Exclusive pricing on additional services, products, and special member events"
  }, {
    icon: Users,
    title: "VIP Experience",
    description: "Complimentary upgrades, guest privileges, and access to members-only events"
  }];
  return <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-juv-gradient-secondary">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-juv-deep-chestnut mb-6 font-serif">
              JUV Membership
            </h1>
            <p className="text-xl text-juv-deep-chestnut/80 leading-relaxed mb-8">
              Your gateway to consistent glow with flexible credits, priority booking, and exclusive member benefits. 
              Invest in yourself with a membership that adapts to your aesthetic goals.
            </p>
            <Button asChild size="lg" className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white px-8 py-4 rounded-full font-semibold">
              <Link to="#comparison">Explore Memberships</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Membership Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-juv-deep-chestnut mb-4 font-serif">
              Why Choose JUV Membership?
            </h2>
            <p className="text-lg text-juv-deep-chestnut/70 max-w-2xl mx-auto">
              More than just treatments—it's a lifestyle of consistent self-care and wellness
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {perks.map((perk, index) => <Card key={index} className="border-juv-rose-clay/20 shadow-luxury hover:shadow-editorial transition-all duration-300 group">
                <CardContent className="p-8 text-center bg-slate-50">
                  <div className="w-16 h-16 bg-juv-soft-blush rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <perk.icon className="w-8 h-8 text-juv-terracotta" />
                  </div>
                  <h3 className="text-xl font-bold text-juv-deep-chestnut mb-4 font-serif">
                    {perk.title}
                  </h3>
                  <p className="text-juv-deep-chestnut/70 leading-relaxed">
                    {perk.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Membership Comparison */}
      <div id="comparison">
        <MembershipComparison />
      </div>

      {/* How Credits Work */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-juv-deep-chestnut mb-4 font-serif">
              How JUV Credits Work
            </h2>
            <p className="text-lg text-juv-deep-chestnut/70 max-w-2xl mx-auto">
              Simple, flexible, and designed around your lifestyle
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-juv-soft-blush rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-juv-terracotta">1</span>
                </div>
                <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">Earn Monthly</h3>
                <p className="text-juv-deep-chestnut/70">Receive credits automatically each month based on your membership tier</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-juv-soft-blush rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-juv-terracotta">2</span>
                </div>
                <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">Spend Flexibly</h3>
                <p className="text-juv-deep-chestnut/70">Use credits for any treatment, save them up, or combine for premium services</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-juv-soft-blush rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-juv-terracotta">3</span>
                </div>
                <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">Never Expire</h3>
                <p className="text-juv-deep-chestnut/70">Rollover unused credits with generous limits—your membership, your timeline</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <MembershipTestimonials />

      {/* Final CTA */}
      <section className="py-16 bg-juv-gradient-secondary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <Sparkles className="w-12 h-12 text-juv-terracotta mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-juv-deep-chestnut mb-6 font-serif">
              Ready to Start Your Glow Journey?
            </h2>
            <p className="text-xl text-juv-deep-chestnut/80 mb-8 leading-relaxed">
              Join thousands of members who've made JUV part of their wellness routine. 
              Cancel anytime, no long-term commitments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white px-8 py-4 rounded-full font-semibold">
                <Link to="/membership/signup" className="flex items-center gap-2">
                  Start Your Membership
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-juv-terracotta text-juv-terracotta hover:bg-juv-terracotta hover:text-white px-8 py-4 rounded-full font-semibold">
                <Link to="/quiz">Take the Quiz First</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Membership;